/**
 * Companies Card
 *
 * Displays extracted companies grouped by relationship type (self, competitor,
 * partner, investor, customer). Color-coded badges and optional details.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { Building2, Shield, Swords, Handshake, Landmark, UserCheck } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import type { RichExtractionCompany } from '@/lib/supabase/edge-functions';
import { useThemeColors } from '@/lib/useThemeColors';

const RELATIONSHIP_CONFIG: Record<string, { color: string; bg: string; icon: typeof Building2 }> = {
  self: { color: '#059669', bg: '#D1FAE5', icon: Shield },
  competitor: { color: '#DC2626', bg: '#FEE2E2', icon: Swords },
  partner: { color: '#2563EB', bg: '#DBEAFE', icon: Handshake },
  investor: { color: '#7C3AED', bg: '#EDE9FE', icon: Landmark },
  customer: { color: '#D97706', bg: '#FEF3C7', icon: UserCheck },
};

const RELATIONSHIP_ORDER = ['self', 'competitor', 'partner', 'investor', 'customer'];

interface CompaniesCardProps {
  companies: RichExtractionCompany[];
}

export function CompaniesCard({ companies }: CompaniesCardProps) {
  const colors = useThemeColors();

  const sorted = [...companies].sort((a, b) => {
    const aIdx = RELATIONSHIP_ORDER.indexOf(a.relationship);
    const bIdx = RELATIONSHIP_ORDER.indexOf(b.relationship);
    return ((aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx)) || a.name.localeCompare(b.name);
  });

  return (
    <Animated.View entering={FadeIn.delay(250).duration(400)}>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Header */}
        <View className="p-4 pb-3">
          <View className="flex-row items-center gap-2.5">
            <View
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{ backgroundColor: '#F0FDF4' }}
            >
              <Building2 size={16} color="#059669" />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                Companies
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {companies.length} identified from deck
              </Text>
            </View>
          </View>
        </View>

        {/* Company list */}
        <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
          {sorted.map((company, i) => {
            const config = RELATIONSHIP_CONFIG[company.relationship] || { color: '#6B7280', bg: '#F3F4F6', icon: Building2 };
            const RelIcon = config.icon;

            return (
              <Animated.View
                key={`${company.name}-${i}`}
                entering={FadeInDown.delay(i * 40).duration(250)}
                className="px-4 py-3"
                style={i < sorted.length - 1 ? { borderBottomWidth: 0.5, borderBottomColor: colors.border.light } : undefined}
              >
                <View className="flex-row items-start gap-3">
                  {/* Icon circle */}
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: config.bg }}
                  >
                    <RelIcon size={16} color={config.color} />
                  </View>

                  <View className="flex-1">
                    {/* Name + relationship badge */}
                    <View className="flex-row items-center gap-2 mb-0.5">
                      <Text style={{ color: colors.text.primary }} className="text-sm font-semibold">
                        {company.name}
                      </Text>
                      <View
                        className="flex-row items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: config.bg }}
                      >
                        <Text style={{ color: config.color }} className="text-xs font-medium">
                          {company.relationship}
                        </Text>
                      </View>
                    </View>

                    {/* Industry */}
                    {company.industry ? (
                      <Text style={{ color: colors.text.secondary }} className="text-xs">
                        {company.industry}
                      </Text>
                    ) : null}

                    {/* Description */}
                    {company.description ? (
                      <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={2}>
                        {company.description}
                      </Text>
                    ) : null}
                  </View>
                </View>
              </Animated.View>
            );
          })}
        </View>
      </View>
    </Animated.View>
  );
}
